import { createSlice } from '@reduxjs/toolkit';
import { defaultUltimas24h } from '../utils/logAuditoria';

// ============================================================================
// Slice para a pagina global de busca de logs de integracao (acessivel via
// menu Configuracoes, perfil AD). E uma busca livre — diferente do modal
// (logsIntegracao.reducer) que escopa por idSimulacao.
//
// Filtros (todos opcionais, mas pelo menos UM entre periodo, idOportunidade
// ou idSimulacao e obrigatorio — validado no backend):
//   - dataInicioFrom / dataInicioTo (datetime ISO)
//   - idOportunidade, idSimulacao
//   - flagSuccesso (true/false/null=todas)
//   - codigoFluxo (multi), tipoEvento (multi)
//
// A busca e disparada APENAS quando o usuario clica em "Buscar"
// (filtros nao redisparam automaticamente). Isso evita scan desnecessario
// enquanto o usuario digita ids.
// ============================================================================

const inicial24h = defaultUltimas24h();

const buildInitialFiltros = () => ({
  dataInicioFrom: inicial24h.from,
  dataInicioTo: inicial24h.to,
  idOportunidade: '',
  idSimulacao: '',
  flagSuccesso: null,
  codigoFluxo: [],
  tipoEvento: []
});

const initialState = {
  filtros: buildInitialFiltros(),
  lista: {
    loading: false,
    error: null,
    data: [],
    total: 0,
    page: 1,
    pageSize: 25,
    buscado: false  // false ate o primeiro clique em Buscar
  },
  sort: { field: 'dataHoraInicio', direction: 'desc' },
  detalhe: {
    loading: false,
    error: null,
    data: null,
    idSelecionado: null
  }
};

const LogsIntegracaoPaginaSlice = createSlice({
  name: 'logsIntegracaoPagina',
  initialState,
  reducers: {
    alterarFiltro: (state, action) => {
      state.filtros = { ...state.filtros, ...action.payload };
    },
    limparFiltros: (state) => {
      state.filtros = buildInitialFiltros();
      state.lista = { ...initialState.lista };
      state.detalhe = { ...initialState.detalhe };
    },
    alterarPaginacao: (state, action) => {
      const { page, pageSize } = action.payload || {};
      if (typeof page === 'number') state.lista.page = page;
      if (typeof pageSize === 'number') state.lista.pageSize = pageSize;
    },
    alterarSort: (state, action) => {
      state.sort = action.payload;
      state.lista.page = 1;
    },
    // Action que o componente dispara ao clicar em "Buscar". O saga
    // responde montando o body a partir do estado atual e dispatchando
    // buscarRequest.
    buscarClick: (state) => {
      state.lista.page = 1;
      state.detalhe = { ...initialState.detalhe };
    },
    buscarRequest: (state) => {
      state.lista.loading = true;
      state.lista.error = null;
      state.lista.buscado = true;
    },
    buscarSuccess: (state, action) => {
      const payload = action.payload?.response?.data ?? {};
      state.lista.loading = false;
      state.lista.error = null;
      state.lista.data = payload.data ?? [];
      state.lista.total = payload.total ?? 0;
    },
    buscarFailure: (state, action) => {
      state.lista.loading = false;
      state.lista.error = action.payload;
      state.lista.data = [];
      state.lista.total = 0;
    },
    selecionarLog: (state, action) => {
      state.detalhe.idSelecionado = action.payload;
      state.detalhe.data = null;
      state.detalhe.error = null;
    },
    detalheRequest: (state) => {
      state.detalhe.loading = true;
      state.detalhe.error = null;
    },
    detalheSuccess: (state, action) => {
      state.detalhe.loading = false;
      state.detalhe.error = null;
      state.detalhe.data = action.payload?.response?.data ?? null;
    },
    detalheFailure: (state, action) => {
      state.detalhe.loading = false;
      state.detalhe.error = action.payload;
      state.detalhe.data = null;
    },
    sairPagina: (state) => {
      // Reset completo ao sair da rota (similar ao limparFiltros mas
      // tambem regenera o default de "ultimas 24h" para a proxima visita).
      const novo24h = defaultUltimas24h();
      state.filtros = {
        ...buildInitialFiltros(),
        dataInicioFrom: novo24h.from,
        dataInicioTo: novo24h.to
      };
      state.lista = { ...initialState.lista };
      state.detalhe = { ...initialState.detalhe };
    }
  }
});

export const {
  alterarFiltro,
  limparFiltros,
  alterarPaginacao,
  alterarSort,
  buscarClick,
  buscarRequest,
  buscarSuccess,
  buscarFailure,
  selecionarLog,
  detalheRequest,
  detalheSuccess,
  detalheFailure,
  sairPagina
} = LogsIntegracaoPaginaSlice.actions;

export default LogsIntegracaoPaginaSlice.reducer;
